import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import Topbar from '../layout/Topbar';
import { useBookStore } from '../../provider/BookStore';
import { useTransactionStore } from '../../provider/TransactionStore';
import { useMasterStore } from '../../provider/MasterStore';
import PreviewImportTable from './PreviewImportTable';
import {
  MAX_ROWS,
  buildPreviewRows,
  computeStatus,
  applyDuplicateDetection,
  normalizeDate,
  normalizeTime,
  normalizeType,
  normalizeAmount,
  dupeKey,
} from '../../utils/csv';

const ImportTransaction = () => {
  const navigate = useNavigate();
  const bookState = useBookStore();
  const book = bookState.book;
  // untuk membuang cache transaksi setelah import berhasil
  const transStore = useTransactionStore();

  const [step, setStep] = useState(1);
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pockets, setPockets] = useState([]);
  const [headerError, setHeaderError] = useState('');
  const [msg, setMsg] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [renderKey, setRenderKey] = useState(0);

  // Data existing (untuk deteksi duplikat) dari DB.
  const existingKeysRef = useRef([]);
  // cache master data (kategori & dompet)
  const masterStore = useMasterStore();

  // Hasil import.
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (book.id === undefined) {
      navigate('/');
      return;
    }
    loadMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMaster = async () => {
    try {
      // via cache store: instan bila cache segar, revalidate bila basi
      const cats = await masterStore.loadCategories(book.id);
      if (cats) {
        setCategories([...cats.debit, ...cats.kredit]);
      }

      const poc = await masterStore.loadPockets(book.id);
      if (poc) {
        setPockets(poc);
      }
    } catch (error) {
      console.log(error.response);
    }
  };

  const fileToText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsText(file, 'UTF-8');
    });
  };

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    setMsg('');
    setHeaderError('');
    setResult(null);

    try {
      const text = await fileToText(file);
      const { rows: parsed, headerError: hErr } = buildPreviewRows(text, {
        categories,
        pockets,
      });

      if (hErr) {
        setHeaderError(hErr);
        setRows([]);
        return;
      }

      if (parsed.length === 0) {
        setHeaderError('Tidak ada baris data yang bisa dibaca.');
        setRows([]);
        return;
      }

      if (parsed.length > MAX_ROWS) {
        setHeaderError(
          `File berisi ${parsed.length} baris, melebihi batas maksimum ${MAX_ROWS} baris per import. Silahkan pecah file Anda.`
        );
        setRows([]);
        return;
      }

      const keys = await fetchExistingKeys(parsed);
      existingKeysRef.current = keys;
      setRows(applyDuplicateDetection(parsed, keys));
      setStep(2);
    } catch (error) {
      setHeaderError('Gagal membaca file. Pastikan format CSV benar.');
    }
  };

  // Ambil kunci transaksi existing pada rentang tanggal file (untuk cek duplikat).
  const fetchExistingKeys = async (parsedRows) => {
    const dates = parsedRows.map((r) => r.date).filter(Boolean);
    if (dates.length === 0) return [];

    const sorted = [...dates].sort();
    const start = sorted[0];
    const end = sorted[sorted.length - 1];

    try {
      const res = await api.post('/transactions', {
        book_id: book.id,
        start_date: start,
        end_date: end,
      });
      const existing = res.data.transactions || [];
      return existing.map((t) =>
        dupeKey(t.date, t.type, t.amount, t.note, t.category_id)
      );
    } catch (error) {
      console.log(error.response);
      return [];
    }
  };

  // Hitung ulang status seluruh baris berdasarkan data terbaru.
  const recomputeRows = (list) => {
    const recalculated = list.map((row) => {
      const next = { ...row };
      next.status = computeStatus(next);
      next.reason = '';
      return next;
    });
    return applyDuplicateDetection(recalculated, existingKeysRef.current);
  };

  const handleRowChange = (index, patch) => {
    setRows((prev) => {
      const next = prev.map((row, i) => {
        if (i !== index) return row;
        const updated = { ...row, ...patch };

        // Normalisasi ulang field bila diedit manual.
        if (patch.date !== undefined) {
          updated.date = normalizeDate(patch.date);
        }
        if (patch.time !== undefined) {
          updated.time = normalizeTime(patch.time);
        }
        if (patch.type !== undefined) {
          updated.type = normalizeType(patch.type);
        }
        if (patch.amount !== undefined) {
          updated.amount = patch.amount === '' ? null : normalizeAmount(patch.amount);
        }
        if (patch.note !== undefined) updated.note = patch.note;
        if (patch.categoryId !== undefined) updated.categoryId = patch.categoryId;
        if (patch.pocketId !== undefined) updated.pocketId = patch.pocketId;

        return updated;
      });
      return recomputeRows(next);
    });
  };

  // Hapus baris dari daftar preview (tidak akan ikut diimport).
  const handleRemoveRow = (index) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return recomputeRows(next);
    });
  };

  const summary = useMemo(() => {
    const counts = { ok: 0, fix: 0, error: 0, dup: 0 };
    rows.forEach((r) => {
      if (counts[r.status] !== undefined) counts[r.status] += 1;
    });
    return counts;
  }, [rows]);

  const handleSubmit = async () => {
    const valid = rows.filter((r) => r.status === 'ok');
    if (valid.length === 0) {
      setMsg('Tidak ada baris yang siap diimport.');
      return;
    }

    setLoading(true);
    setMsg('');
    try {
      const payload = valid.map((r) => ({
        row: r.row,
        date: r.date,
        time: r.time || '00:00',
        type: r.type,
        amount: r.amount,
        note: r.note,
        category_id: r.categoryId,
        pocket_id: r.pocketId || null,
      }));

      const res = await api.post('/transaction/import', {
        book_id: book.id,
        transactions: payload,
      });

      // data sudah berubah di server -> buang cache buku ini agar
      // halaman Transaksi & Statistik mengambil data terbaru
      transStore.invalidate(book.id);

      setResult(res.data);
      setStep(3);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        'Gagal melakukan import. Silahkan coba lagi.';
      setMsg(message);
    }
    setLoading(false);
  };

  const reset = () => {
    setRows([]);
    setHeaderError('');
    setMsg('');
    setResult(null);
    setStep(1);
    setRenderKey((k) => k + 1);
  };

  const goToTransactions = () => {
    navigate('/transactions');
  };

  return (
    <div>
      <Topbar
        title={'Import CSV'}
        back={() => navigate('/book')}
        isLoading={isLoading}
      />
      <div className='container p-5'>
        <div className='columns is-centered'>
          <div className='column is-9-widescreen is-10-desktop is-full-tablet'>
            <p className='is-size-6 mb-4 ml-4'>Buku Kas : {book.title}</p>
            {renderStep()}
          </div>
        </div>
      </div>
    </div>
  );

  function renderStep() {
    if (step === 1) return renderStep1();
    if (step === 2) return renderStep2();
    return renderStep3();
  }

  function renderStep1() {
    return (
      <div className='card'>
        <header className='card-header'>
          <p className='card-header-title'>Import Transaksi dari CSV</p>
        </header>
        <div className='card-content'>
          <p className='mb-4'>
            Unggah file <b>.csv</b> berisi transaksi untuk buku kas
            <b> {book.title}</b>. Pastikan file memakai header:
          </p>
          <pre className='import-sample'>
            tanggal,waktu,jenis,jumlah,keterangan,kategori,dompet
          </pre>

          <div className='field'>
            <div className='file is-boxed is-fullwidth'>
              <label className='file-label'>
                <input
                  key={renderKey}
                  className='file-input'
                  type='file'
                  accept='.csv,text/csv'
                  onChange={handleFile}
                />
                <span className='file-cta'>
                  <span className='file-icon'>
                    <i className='fa fa-upload'></i>
                  </span>
                  <span className='file-label'>Pilih file CSV...</span>
                </span>
              </label>
            </div>
          </div>

          <a
            className='button is-light is-small mt-2'
            href='/template-import-transaksi.csv'
            download
          >
            <span className='icon'><i className='fa fa-download'></i></span>
            <span>Unduh template CSV</span>
          </a>

          {headerError && (
            <p className='has-text-danger mt-4'>{headerError}</p>
          )}
          {msg && <p className='has-text-danger mt-4'>{msg}</p>}
        </div>
      </div>
    );
  }

  function renderStep2() {
    const total = rows.length;
    return (
      <div className='card'>
        <header className='card-header'>
          <p className='card-header-title'>Preview & Perbaikan Data</p>
        </header>
        <div className='card-content'>
          <p className='is-size-7 mb-3'>
            Total <b>{total}</b> baris —{' '}
            <span className='has-text-success'>{summary.ok} siap</span>,{' '}
            <span className='has-text-warning'>{summary.fix} perlu perbaikan</span>,{' '}
            <span className='has-text-danger'>{summary.error} error</span>,{' '}
            <span className='has-text-danger'>{summary.dup} duplikat</span>.
          </p>
          <p className='is-size-7 mb-3 has-text-grey'>
            Kategori wajib dipilih. Dompet boleh dikosongkan. Baris yang tidak
            valid/duplikat tidak akan diimport.
          </p>

          {rows.length > 0 && (
            <PreviewImportTable
              rows={rows}
              categories={categories}
              pockets={pockets}
              onChange={handleRowChange}
              onRemove={handleRemoveRow}
            />
          )}

          {msg && <p className='has-text-danger mt-4'>{msg}</p>}
        </div>
        <footer className='card-footer'>
          <button className='card-footer-item' onClick={reset}>
            Ganti File
          </button>
          <button
            className='card-footer-item has-text-weight-bold has-text-success'
            disabled={summary.ok === 0 || isLoading}
            onClick={handleSubmit}
          >
            {isLoading ? 'Mengimport...' : `Import ${summary.ok} Baris`}
          </button>
        </footer>
      </div>
    );
  }

  function renderStep3() {
    const s = (result && result.summary) || { total: 0, inserted: 0, failed: 0 };
    const errors = (result && result.errors) || [];
    return (
      <div className='card'>
        <header className='card-header'>
          <p className='card-header-title'>Hasil Import</p>
        </header>
        <div className='card-content'>
          <div className='is-clearfix mb-2'>
            <span className='is-pulled-left'>Total diproses</span>
            <span className='is-pulled-right'>{s.total}</span>
          </div>
          <div className='is-clearfix mb-2'>
            <span className='is-pulled-left has-text-success'>Berhasil</span>
            <span className='is-pulled-right has-text-success'>{s.inserted}</span>
          </div>
          <div className='is-clearfix mb-2'>
            <span className='is-pulled-left has-text-danger'>Gagal</span>
            <span className='is-pulled-right has-text-danger'>{s.failed}</span>
          </div>

          {errors.length > 0 && (
            <>
              <hr />
              <p className='has-text-weight-semibold mb-2'>Detail baris gagal:</p>
              <ul className='import-error-list'>
                {errors.map((err, i) => (
                  <li key={i} className='is-size-7'>
                    Baris {err.row}: {err.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <footer className='card-footer'>
          <button className='card-footer-item' onClick={reset}>
            Import Lagi
          </button>
          <button
            className='card-footer-item has-text-weight-bold has-text-success'
            onClick={goToTransactions}
          >
            Lihat Transaksi
          </button>
        </footer>
      </div>
    );
  }
};

export default ImportTransaction;
